/* eslint-disable perfectionist/sort-imports -- eslint conflicts: path order vs type-before-value across two modules */
import type { TAudienceWebRtcIceServer } from '@lucimark/shared/models/audience-webrtc-ice'
import { DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS } from '@lucimark/shared/models/audience-webrtc-ice'
import type { TAudienceWebRtcSignalRequestBody } from '@lucimark/shared/models/audience-webrtc-signal'
import { audienceWebRtcSignalPayloadSchema } from '@lucimark/shared/models/audience-webrtc-signal'
/* eslint-enable perfectionist/sort-imports */

import { invalidateAudienceVideoInputDevicesCache, suppressAudienceVideoInputDeviceProbeForMs } from '@/helpers/enumerateAudienceVideoInputDevices'

/** Why the player released the camera / peer connection. */
export type TAudienceWebRtcPreviewTeardownReason = 'new_offer' | 'operator_bye' | 'peer_connection_closed' | 'peer_connection_failed' | 'set_remote_offer_failed' | 'get_user_media_failed' | 'create_answer_failed' | 'missing_answer_sdp' | 'post_answer_failed' | 'external_reset'

export interface IAudienceWebRtcPreviewSession {
  handleRemoteSignal: (payload: unknown) => Promise<void>
  teardown: () => Promise<void>
  /** True while a preview peer connection is active (avoid a second `getUserMedia` for audience analytics). */
  isPreviewActive: () => boolean
}

function toRtcIceServers(servers: TAudienceWebRtcIceServer[]): RTCConfiguration['iceServers'] {
  return servers.map(s => ({
    urls: s.urls,
    ...(s.username != null && s.username !== '' ? { username: s.username } : {}),
    ...(s.credential != null && s.credential !== '' ? { credential: s.credential } : {})
  }))
}

export function createAudienceWebRtcPreviewSession(deps: {
  postSignal: (body: TAudienceWebRtcSignalRequestBody) => Promise<void>
  isPreviewEnabled: () => boolean
  /** Preferred camera `deviceId` from server (`audienceVideoInputDeviceId`); empty = default device. */
  getVideoInputDeviceId: () => string | null
  /** ICE servers from registration `tenant.audienceWebRtcIceServers`; falls back to public STUN. */
  getIceServers: () => TAudienceWebRtcIceServer[]
}): IAudienceWebRtcPreviewSession {
  let pc: RTCPeerConnection | null = null
  let localStream: MediaStream | null = null
  let activeSessionId: string | null = null
  const pendingRemoteIce: RTCIceCandidateInit[] = []
  /** One signal at a time: a `bye` during `getUserMedia` must not interleave or we can orphan tracks (Chrome). */
  let signalHandlingChain: Promise<void> = Promise.resolve()

  function runSerialized(fn: () => Promise<void>): Promise<void> {
    const run = signalHandlingChain.then(fn)
    signalHandlingChain = run.catch(() => {})
    return run
  }

  function stopMediaStreamTracks(stream: MediaStream | null): void {
    if (stream == null) {
      return
    }
    for (const t of stream.getTracks()) {
      try {
        t.stop()
      }
      catch {
        // ignore
      }
    }
  }

  /** Close a peer connection started for an offer if setup was aborted (e.g. concurrent `teardown()`). */
  function abandonOfferAttempt(conn: RTCPeerConnection, offerSessionId: string): void {
    conn.onconnectionstatechange = null
    conn.onicecandidate = null
    try {
      conn.close()
    }
    catch {
      // ignore
    }
    if (pc === conn) {
      pc = null
    }
    if (activeSessionId === offerSessionId) {
      activeSessionId = null
    }
    stopMediaStreamTracks(localStream)
    localStream = null
  }

  async function teardown(_reason: TAudienceWebRtcPreviewTeardownReason) {
    let releasedMedia = false
    pendingRemoteIce.length = 0
    if (pc != null) {
      releasedMedia = true
      pc.onconnectionstatechange = null
      pc.onicecandidate = null
      try {
        for (const sender of pc.getSenders()) {
          try {
            sender.track?.stop()
          }
          catch {
            // ignore
          }
        }
        for (const tr of pc.getTransceivers()) {
          try {
            tr.stop()
          }
          catch {
            // ignore
          }
        }
      }
      catch {
        // ignore
      }
      try {
        pc.close()
      }
      catch {
        // ignore
      }
      pc = null
    }
    if (localStream != null) {
      releasedMedia = true
      for (const t of localStream.getTracks()) {
        try {
          t.stop()
        }
        catch {
          // ignore
        }
      }
      localStream = null
    }
    activeSessionId = null
    if (releasedMedia) {
      invalidateAudienceVideoInputDevicesCache()
      suppressAudienceVideoInputDeviceProbeForMs(10_000)
    }
  }

  async function flushPendingIce() {
    if (pc == null) {
      return
    }
    const q = pendingRemoteIce.splice(0, pendingRemoteIce.length)
    for (const init of q) {
      try {
        await pc.addIceCandidate(init)
      }
      catch {
        // ignore invalid / late candidates
      }
    }
  }

  return {
    isPreviewActive: () => activeSessionId != null && pc != null,
    async handleRemoteSignal(payload: unknown) {
      await runSerialized(async () => {
        const parsed = audienceWebRtcSignalPayloadSchema.safeParse(payload)
        if (!parsed.success) {
          return
        }
        const p = parsed.data
        if (p.kind === 'bye') {
          if (p.sessionId === activeSessionId) {
            await teardown('operator_bye')
          }
          return
        }
        if (!deps.isPreviewEnabled()) {
          return
        }

        if (p.kind === 'offer' && p.sdp != null) {
          await teardown('new_offer')
          activeSessionId = p.sessionId
          const thisOfferSessionId = p.sessionId
          const configured = deps.getIceServers()
          const iceServers = toRtcIceServers(configured.length > 0 ? configured : DEFAULT_AUDIENCE_WEBRTC_STUN_SERVERS)
          const connection = new RTCPeerConnection({ iceServers })
          pc = connection
          let hadPeerConnected = false
          connection.onconnectionstatechange = () => {
            if (connection.connectionState === 'connected') {
              hadPeerConnected = true
            }
            if (!hadPeerConnected) {
              return
            }
            if (connection.connectionState === 'failed') {
              void teardown('peer_connection_failed')
            }
            else if (connection.connectionState === 'closed') {
              void teardown('peer_connection_closed')
            }
          }
          connection.onicecandidate = (ev) => {
            if (ev.candidate == null || activeSessionId !== thisOfferSessionId) {
              return
            }
            void deps.postSignal({
              sessionId: thisOfferSessionId,
              kind: 'ice',
              candidate: {
                candidate: ev.candidate.candidate,
                sdpMid: ev.candidate.sdpMid,
                sdpMLineIndex: ev.candidate.sdpMLineIndex
              }
            }).catch(() => {})
          }
          try {
            await connection.setRemoteDescription({ type: 'offer', sdp: p.sdp })
          }
          catch {
            await teardown('set_remote_offer_failed')
            return
          }
          if (activeSessionId !== thisOfferSessionId || pc !== connection) {
            abandonOfferAttempt(connection, thisOfferSessionId)
            return
          }
          await flushPendingIce()
          if (activeSessionId !== thisOfferSessionId || pc !== connection) {
            abandonOfferAttempt(connection, thisOfferSessionId)
            return
          }
          const trimmedDeviceId = deps.getVideoInputDeviceId()?.trim() ?? ''
          let acquired: MediaStream
          try {
            const videoConstraints: MediaTrackConstraints | boolean = trimmedDeviceId !== ''
              ? { deviceId: { exact: trimmedDeviceId } }
              : true
            acquired = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false })
          }
          catch {
            if (trimmedDeviceId === '') {
              await teardown('get_user_media_failed')
              return
            }
            try {
              acquired = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
            }
            catch {
              await teardown('get_user_media_failed')
              return
            }
          }
          if (activeSessionId !== thisOfferSessionId || pc !== connection) {
            stopMediaStreamTracks(acquired)
            abandonOfferAttempt(connection, thisOfferSessionId)
            return
          }
          localStream = acquired
          for (const track of localStream.getTracks()) {
            connection.addTrack(track, localStream)
          }
          let answer: RTCSessionDescriptionInit
          try {
            answer = await connection.createAnswer()
            await connection.setLocalDescription(answer)
          }
          catch {
            await teardown('create_answer_failed')
            return
          }
          if (answer.sdp == null) {
            await teardown('missing_answer_sdp')
            return
          }
          if (activeSessionId !== thisOfferSessionId || pc !== connection) {
            abandonOfferAttempt(connection, thisOfferSessionId)
            return
          }
          try {
            await deps.postSignal({ sessionId: thisOfferSessionId, kind: 'answer', sdp: answer.sdp })
          }
          catch {
            await teardown('post_answer_failed')
          }
        }
        else if (p.kind === 'ice' && p.candidate != null) {
          if (p.sessionId !== activeSessionId || pc == null) {
            return
          }
          const init: RTCIceCandidateInit = {
            candidate: p.candidate.candidate,
            sdpMid: p.candidate.sdpMid ?? undefined,
            sdpMLineIndex: p.candidate.sdpMLineIndex ?? undefined
          }
          if (pc.remoteDescription == null) {
            pendingRemoteIce.push(init)
          }
          else {
            try {
              await pc.addIceCandidate(init)
            }
            catch {
              // ignore
            }
          }
        }
      })
    },
    teardown: async () => {
      await teardown('external_reset')
    }
  }
}
